import { Query } from '@apollo/client/react/components'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'

import {
  getConnectionId,
  isAchEnabled,
  isSepaEnabled,
  transformPaymentRecords,
} from './payments.transformations'

const READY_STATUS = 7
export const STRIPE_PROVIDER_NAME = 'stripe'
export const REACH_PROVIDER_NAME = 'reach'

export const connectedProvidersQuery = atlas`#graphql
  query accountPaymentProviders($destinationAccountId: ID!) {
    accountPaymentProviders(destinationAccountId: $destinationAccountId) {
      externalIdentifier
      paymentProvider
      isSepaEnabled
      isAchEnabled
      retailersCanSeeInvoices
      sendBrandMailPaymentProcessed
      sendRetailerMailPaymentProcessed
      brandNotificationEmail
    }
  }
`

export const orderPaymentRecords = atlas`#graphql
  query PaymentRecord($orderIds: [ID!]!, $accountId: ID!) {
    paymentRecords(orderIds: $orderIds, accountId: $accountId) {
      id
      orderId
      amount
      currency
      status
      created
      localAmount
      localCurrency
      paymentMethod {
        type
        last4
        billingName
        billingEmail
        ... on BankAccount {
          bankName
        }
        ... on CreditCard {
          brand
        }
      }
    }
  }
`

const invoiceStatusesQuery = atlas`#graphql
  query invoices($accountId: ID!, $orderIds: [ID!]!) {
    invoices(accountId: $accountId, orderIds: $orderIds) {
      orderId
      sentOn
      sentTo
    }
  }
`

atlas`#graphql
query PaymentInfoOrders($filters: OrderFilterInput!) {
  orders(filters: $filters) {
    edges {
      node {
        id
        paymentInfo {
          paymentStatus
          requestStatus
          remainingBalance
        }
      }
    }
  }
}
`

export const withConnectedProviders = (WrappedComponent) => (props) => {
  const { destinationAccountId, canViewPaymentProviders, params } = props
  // The property featureFlagJoorPay works with Anonymous Invoice
  let { featureFlagJoorPay } = props
  if (!props.order) {
    // If there is no order available we are in settings page, use logged in context
    const { joorPayUseReach } = useFlags()
    featureFlagJoorPay = joorPayUseReach
  }

  const providerName = featureFlagJoorPay
    ? REACH_PROVIDER_NAME
    : STRIPE_PROVIDER_NAME

  return (
    <Query
      client={atlasClient}
      query={connectedProvidersQuery}
      variables={{
        destinationAccountId,
      }}
      skip={!destinationAccountId || !canViewPaymentProviders}
      context={{
        ...(params.uuid !== undefined && {
          headers: {
            'invoice-auth': params.uuid,
          },
        }),
      }}
    >
      {({ loading, error, data, networkStatus, refetch }) => {
        const accountPaymentProviders =
          !loading && !error && data ? data.accountPaymentProviders : []
        const connectedAccount = getConnectionId(
          accountPaymentProviders,
          providerName,
        )

        const customProps = {
          ...props,
          connectedProvidersReady: networkStatus === READY_STATUS,
          connectedProvidersLoading: loading,
          connectedProviders: accountPaymentProviders,
          isConnectedToProvider: Boolean(connectedAccount),
          connectedAccount,
          destinationAccountId,
          isSepaEnabled: isSepaEnabled(accountPaymentProviders, providerName),
          isAchEnabled: isAchEnabled(accountPaymentProviders, providerName),
          providerName,
          refetchConnectedProviders: refetch,
        }

        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}

export const withOrderPaymentRecords = (WrappedComponent) => (props) => {
  const { accountId, orders, canViewPaymentRecords, params } = props
  const id = accountId
    ? toGlobalId('Account', accountId)
    : get(orders, '[0].retailer.id')

  return (
    <Query
      client={atlasClient}
      query={orderPaymentRecords}
      variables={{
        accountId: id,
        orderIds: orders.map((order) => order.id),
      }}
      skip={isEmpty(orders) || !canViewPaymentRecords}
      context={{
        ...(params.uuid !== undefined && {
          headers: {
            'invoice-auth': params.uuid,
          },
        }),
      }}
    >
      {({ data, networkStatus, refetch }) => {
        const paymentRecords = data?.paymentRecords
        const orderPaymentData = paymentRecords
          ? transformPaymentRecords(paymentRecords)
          : undefined
        const customProps = {
          ...props,
          orderPaymentData,
          orderPaymentRecordsReady: networkStatus === READY_STATUS,
          refetchPaymentRecords: refetch,
        }

        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}

export const getInvoiceStatusVariables = (
  uuid,
  orders,
  destinationAccountId,
) => {
  let accountId

  if (uuid !== undefined) {
    accountId = get(orders, '[0].retailer.id')
  } else {
    accountId = destinationAccountId
      ? toGlobalId('Account', fromGlobalId(destinationAccountId).id)
      : ''
  }

  return {
    accountId,
    orderIds: map(orders, (order) => order.id),
  }
}

export const withInvoicesStatus = (WrappedComponent) => (props) => {
  const {
    destinationAccountId,
    orders,
    canViewInvoiceStatus,
    isConnectedToProvider,
    params,
  } = props

  return (
    <Query
      client={atlasClient}
      query={invoiceStatusesQuery}
      variables={getInvoiceStatusVariables(
        params.uuid,
        orders,
        destinationAccountId,
      )}
      skip={
        !canViewInvoiceStatus ||
        !isConnectedToProvider ||
        !get(orders, 'length')
      }
      context={{
        ...(params.uuid !== undefined && {
          headers: {
            'invoice-auth': params.uuid,
          },
        }),
      }}
    >
      {({ loading, error, data }) => {
        const invoices = !loading && !error && data ? data.invoices : []
        const customProps = {
          ...props,
          invoices,
          invoiceStatusQueryLoading: loading,
        }

        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}
